import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useNavigate } from 'react-router-dom';
import DataOps from '../../../../data/DataOps';
import { SessionInterface } from '../../../../data/SessionInterface';

interface CampMapPopupProps {
    camp: any;
    onClose: () => void;
}

const CampMapPopup: React.FC<CampMapPopupProps> = ({ camp, onClose }) => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<SessionInterface[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchSessions = async () => {
            try {
                if (!camp?.campId) {
                    setSessions([]);
                    setIsLoading(false);
                    return;
                }
                const sessionsMap = await DataOps.fetchSessionsForCamp(camp.campId);
                const sessionData = Object.values(sessionsMap) as SessionInterface[];
                const validSessions = sessionData
                    .filter(session => session && !(session as any).isHidden)
                    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                    .slice(0, 2);
                setSessions(validSessions);
            } catch (error) {
                console.error('Error fetching sessions:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSessions();
    }, [camp?.campId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        // If it's already a formatted string like "Jan 15th", convert to MM/DD/YYYY
        if (typeof dateString === 'string' && (dateString.includes('th') || dateString.includes('st') || dateString.includes('nd') || dateString.includes('rd'))) {
            const dateMatch = dateString.match(/(\w+)\s+(\d+)/);
            if (dateMatch) {
                const month = dateMatch[1];
                const day = parseInt(dateMatch[2]);
                const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
                if (monthIndex !== -1) {
                    const currentYear = new Date().getFullYear();
                    const formattedMonth = String(monthIndex + 1).padStart(2, '0');
                    const formattedDay = String(day).padStart(2, '0');
                    return `${formattedMonth}/${formattedDay}/${currentYear}`;
                }
            }
            return dateString;
        }
        
        try {
            // Handle different date formats
            let date;
            if (typeof dateString === 'string') {
                // If already in MM/DD/YYYY format, return as is
                if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    return dateString;
                }
                
                // Try parsing as ISO string first
                date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    // Try parsing as MM/DD/YYYY format
                    const parts = dateString.split('/');
                    if (parts.length === 3) {
                        date = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
                    } else {
                        return 'N/A';
                    }
                }
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) return 'N/A';
            
            // Convert to MM/DD/YYYY format
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            
            return `${month}/${day}/${year}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const formatPrice = (price: string) => {
        const numPrice = parseFloat(price);
        if (isNaN(numPrice)) return 'Contact for pricing';
        return `$${numPrice.toLocaleString()}`;
    };

    const getAgeRange = (startAge: string, endAge: string) => {
        if (!startAge || !endAge) return 'All ages';
        return `${startAge}-${endAge}`;
    };

    const handleViewCamp = () => {
        navigate(`/camps/${camp.campId}`);
        onClose();
    };

    return (
        <View style={styles.container}>
            {/* Header with close button */}
            <View style={styles.header}>
                <View style={styles.headerContent}>
                    <Text style={styles.campName} numberOfLines={2}>
                        {camp.campName}
                    </Text>
                    <Text style={styles.location}>
                        {camp.city}, {camp.state}
                    </Text>
                </View>
                <Pressable style={styles.closeButton} onPress={onClose}>
                    <MaterialCommunityIcons name="close" size={20} color="#666" />
                </Pressable>
            </View>

            {/* Camp info badges */}
            <View style={styles.badgesContainer}>
                <View style={styles.badge}>
                    <MaterialCommunityIcons name="star" size={14} color="#FFD700" />
                    <Text style={styles.badgeText}>
                        {camp.rating ? camp.rating.toFixed(1) : '5.0'}
                    </Text>
                </View>
                <View style={styles.badge}>
                    <MaterialCommunityIcons name="account-group" size={14} color="#4CAF50" />
                    <Text style={styles.badgeText}>
                        {camp.campFollowers ? camp.campFollowers.length : 0} followers
                    </Text>
                </View>
                <View style={styles.badge}>
                    <MaterialCommunityIcons name="calendar-check" size={14} color="#2196F3" />
                    <Text style={styles.badgeText}>
                        {sessions.length} sessions
                    </Text>
                </View>
            </View>

            {/* Sessions section */}
            {!isLoading && sessions.length > 0 ? (
                <View style={styles.sessionsContainer}>
                    <Text style={styles.sessionsTitle}>Upcoming Sessions</Text>
                    
                    {/* Table Header */}
                    <View style={styles.tableHeader}>
                        <View style={styles.headerCell}>
                            <Text style={styles.tableHeaderText}>Session</Text>
                        </View>
                        <View style={styles.headerCell}>
                            <Text style={styles.tableHeaderText}>Schedule</Text>
                        </View>
                        <View style={styles.compactHeaderCell}>
                            <Text style={styles.tableHeaderText}>Ages</Text>
                        </View>
                        <View style={styles.compactHeaderCell}>
                            <Text style={styles.tableHeaderText}>Price</Text>
                        </View>
                        <View style={styles.headerCell}>
                            <Text style={styles.tableHeaderText}>Status</Text>
                        </View>
                    </View>
                    
                    {/* Table Rows */}
                    {sessions.map((session, index) => (
                        <View key={index} style={[
                            styles.tableRow,
                            index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
                        ]}>
                            <View style={styles.tableCell}>
                                <Text style={styles.cellText} numberOfLines={1}>
                                    {session.title || 'Camp Session'}
                                </Text>
                            </View>
                            <View style={styles.scheduleCell}>
                                <Text style={styles.dateText}>
                                    {formatDate(session.startDate)} - {formatDate(session.endDate)}
                                </Text>
                                <Text style={styles.timeText}>
                                    {session.startTime} - {session.endTime}
                                </Text>
                            </View>
                            <View style={styles.compactCell}>
                                <Text style={styles.cellText}>
                                    {getAgeRange(session.startAge, session.endAge)}
                                </Text>
                            </View>
                            <View style={styles.compactCell}>
                                <Text style={styles.cellText}>
                                    {formatPrice(session.price)}
                                </Text>
                            </View>
                            <View style={styles.tableCell}>
                                <View style={[
                                    styles.statusBadge,
                                    { backgroundColor: session.registrationStatus === 'REGISTER' ? '#4CAF50' : '#FF9800' }
                                ]}>
                                    <Text style={styles.statusText}>
                                        {session.registrationStatus === 'REGISTER' ? 'Open' : 'Waitlist'}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    ))}
                </View>
            ) : !isLoading ? (
                <View style={styles.noSessions}>
                    <MaterialCommunityIcons name="calendar-remove" size={24} color="#ccc" />
                    <Text style={styles.noSessionsText}>No upcoming sessions</Text>
                </View>
            ) : (
                <View style={styles.loading}>
                    <Text style={styles.loadingText}>Loading sessions...</Text>
                </View>
            )}

            {/* Action button */}
            <Pressable style={styles.viewButton} onPress={handleViewCamp}>
                <Text style={styles.viewButtonText}>View Full Camp Details</Text>
                <MaterialCommunityIcons name="arrow-right" size={16} color="white" />
            </Pressable>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: 500,
        backgroundColor: 'white',
        borderRadius: 16,
        padding: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
        maxHeight: 400,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 12,
    },
    headerContent: {
        flex: 1,
        marginRight: 8,
    },
    campName: {
        fontSize: 18,
        fontWeight: '700',
        color: '#1a1a1a',
        lineHeight: 22,
        marginBottom: 4,
    },
    location: {
        fontSize: 14,
        color: '#666',
        fontWeight: '500',
    },
    closeButton: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#f5f5f5',
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgesContainer: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: 16,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    badgeText: {
        fontSize: 12,
        color: '#666',
        fontWeight: '500',
    },
    sessionsContainer: {
        marginBottom: 12,
    },
    sessionsTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1a1a1a',
        marginBottom: 12,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    tableHeaderText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        textAlign: 'center',
    },
    headerCell: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    compactHeaderCell: {
        flex: 0.6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 6,
        marginBottom: 2,
        alignItems: 'center',
    },
    tableRowEven: {
        backgroundColor: '#ffffff',
    },
    tableRowOdd: {
        backgroundColor: '#f8f9fa',
    },
    tableCell: {
        fontSize: 12,
        color: '#495057',
        flex: 1,
        textAlign: 'center',
        paddingHorizontal: 4,
    },
    compactCell: {
        fontSize: 12,
        color: '#495057',
        flex: 0.6,
        textAlign: 'center',
        paddingHorizontal: 2,
    },
    cellText: {
        fontSize: 12,
        color: '#495057',
        textAlign: 'center',
    },
    scheduleCell: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 4,
    },
    dateText: {
        fontSize: 12,
        color: '#495057',
        fontWeight: '500',
        textAlign: 'center',
    },
    timeText: {
        fontSize: 11,
        color: '#6c757d',
        textAlign: 'center',
        marginTop: 2,
    },
    statusBadge: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 6,
        alignSelf: 'center',
        minWidth: 40,
        alignItems: 'center',
    },
    statusText: {
        fontSize: 9,
        color: 'white',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },

    noSessions: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 8,
    },
    noSessionsText: {
        fontSize: 14,
        color: '#999',
        fontWeight: '500',
    },
    loading: {
        alignItems: 'center',
        paddingVertical: 20,
    },
    loadingText: {
        fontSize: 14,
        color: '#999',
    },
    viewButton: {
        backgroundColor: '#3C8248',
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 16,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    viewButtonText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '600',
    },
});

export default CampMapPopup; 